/**
 * Inserts items from an array into a new object indexed by id
 *
 * Example:
 * ```typescript
 * const items = [
 *   { id: '1', name: 'foo' },
 *   { id: '2', name: 'bar' }
 * ]
 *
 * const result = insertItems(items)
 * // { '1': { id: '1', name: 'foo' }, '2': { id: '2', name: 'bar' } }
 * ```
 *
 * @param items - Array of items with id property
 * @returns Object with items indexed by their id
 */
export const insertItems = <T extends { id: string }>(
  items: T[]
): Record<string, T> => {
  return items.reduce((acc, item) => {
    acc[item.id] = item

    return acc
  }, {} as Record<string, T>)
}

/**
 * Adds or updates an item in an object (upsert)
 *
 * Example:
 * ```typescript
 * const existing = { '1': { id: '1', name: 'foo' } }
 * const newItem = { id: '2', name: 'bar' }
 *
 * const result = upsertItem(existing, newItem)
 * // { '1': { id: '1', name: 'foo' }, '2': { id: '2', name: 'bar' } }
 * ```
 *
 * @param existingObject - Existing object with items
 * @param item - Item to add or update
 * @returns New object with item added/updated
 */
export const upsertItem = <T extends { id: string }>(
  existingObject: Record<string, T>,
  item: T
): Record<string, T> => {
  return {
    ...existingObject,
    [item.id]: item
  }
}

/**
 * Adds or updates multiple items in an object (batch upsert)
 *
 * Example:
 * ```typescript
 * const existing = { '1': { id: '1', name: 'foo' } }
 * const newItems = [
 *   { id: '2', name: 'bar' },
 *   { id: '3', name: 'baz' }
 * ]
 *
 * const result = upsertItems(existing, newItems)
 * { '1': { id: '1', name: 'foo' },
 *   '2': { id: '2', name: 'bar' },
 *   '3': { id: '3', name: 'baz' }
 * }
 * ```
 *
 * @param existingObject - Existing object with items
 * @param items - Items to add or update
 * @returns New object with items added/updated
 */
export const upsertItems = <T extends { id: string }>(
  existingObject: Record<string, T>,
  items: T[]
): Record<string, T> => {
  return {
    ...existingObject,
    ...insertItems(items)
  }
}

/**
 * Removes an item from an object by id
 *
 * Example:
 * ```typescript
 * const existing = {
 *   '1': { id: '1', name: 'foo' },
 *   '2': { id: '2', name: 'bar' }
 * }
 *
 * const result = removeItem(existing, '1')
 * // { '2': { id: '2', name: 'bar' } }
 * ```
 *
 * @param existingObject - Existing object with items
 * @param id - ID of item to remove
 * @returns New object with item removed
 */
export const removeItem = <T extends { id: string }>(
  existingObject: Record<string, T>,
  id: string
): Record<string, T> => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { [id]: _removed, ...rest } = existingObject

  return rest
}

/**
 * Removes multiple items from an object by ids
 *
 * Example:
 * ```typescript
 * const existing = {
 *   '1': { id: '1', name: 'foo' },
 *   '2': { id: '2', name: 'bar' },
 *   '3': { id: '3', name: 'baz' }
 * }
 *
 * const result = removeItems(existing, ['1', '3'])
 * // { '2': { id: '2', name: 'bar' } }
 * ```
 *
 * @param existingObject - Existing object with items
 * @param ids - IDs of items to remove
 * @returns New object with items removed
 */
export const removeItems = <T extends { id: string }>(
  existingObject: Record<string, T>,
  ids: string[]
): Record<string, T> => {
  const idsToRemove = new Set(ids)

  return Object.entries(existingObject).reduce((acc, [key, value]) => {
    if (!idsToRemove.has(key)) {
      acc[key] = value
    }

    return acc
  }, {} as Record<string, T>)
}

/**
 * Converts an object indexed by id to a typed array
 *
 * Example:
 * ```typescript
 * const obj = {
 *   '1': { id: '1', name: 'foo' },
 *   '2': { id: '2', name: 'bar' }
 * }
 *
 * const result = toArray(obj)
 * // [{ id: '1', name: 'foo' }, { id: '2', name: 'bar' }]
 * ```
 *
 * @param object - Object indexed by id
 * @returns Array of items
 */
export const toArray = <T extends { id: string }>(
  object: Record<string, T> | undefined | null
): T[] => {
  if (!object) return []

  return Object.values(object)
}
